import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, Signal, effect, inject, signal } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class DocumentTitleService {
    private readonly _doc = inject(DOCUMENT);
    private readonly _destroyRef = inject(DestroyRef);

    private readonly _hidden = signal<boolean>(this._doc.hidden);
    private readonly _activeCount = signal<Signal<number> | null>(null);
    private _baseTitle = this._doc.title;
    private _onVisibilityChange = (): void => this._hidden.set(this._doc.hidden);

    constructor() {
        this._doc.addEventListener('visibilitychange', this._onVisibilityChange);
        this._destroyRef.onDestroy(() => {
            this._doc.removeEventListener('visibilitychange', this._onVisibilityChange);
        });

        effect(() => {
            const source = this._activeCount();
            const count = source ? source() : 0;
            const hidden = this._hidden();
            this._render(count, hidden);
        });
    }

    /**
     * Bind a count signal as the source for the (N) prefix. Pass null to detach.
     * Captures the current document.title as the base — call this when entering a page.
     */
    bind(countSignal: Signal<number> | null): void {
        this._baseTitle = this._stripPrefix(this._doc.title);
        this._activeCount.set(countSignal);
    }

    private _render(count: number, hidden: boolean): void {
        if (count > 0 && hidden) {
            this._doc.title = `(${ count }) ${ this._baseTitle }`;
        } else {
            this._doc.title = this._baseTitle;
        }
    }

    private _stripPrefix(title: string): string {
        return title.replace(/^\(\d+\)\s+/, '');
    }
}
